/**
 * Helpers around the browser Push API + the app's /api/push endpoints.
 *
 * Lifecycle:
 *   1. registerServiceWorker() — once on app boot
 *   2. enablePushNotifications() — called from the contractor "Enable
 *      reminders" button. Asks the OS for permission, gets a Web Push
 *      subscription, posts it to the backend.
 *   3. disablePushNotifications() — unsubscribe + tell the backend to drop it.
 *
 * Browser notes:
 *   - iOS Safari: only works when the app is added to the home screen
 *     ("Add to Home Screen" → installs as a PWA). Plain Safari can't push.
 *   - Most desktop browsers: works directly, no install needed.
 *   - Permission must be requested in response to a user gesture (a click).
 */
import { api } from '../api';

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager'   in window     &&
    'Notification'  in window
  );
}

export function notificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/** Register sw.js. Idempotent — safe to call on every page load. */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    // When a new worker takes control (a fresh deploy activated), reload once so
    // the open tab swaps to the new bundle without the user refreshing by hand.
    // Guard against loops, and skip the very first install (no prior controller).
    if (navigator.serviceWorker.controller && !window.__actsSwReloadHooked) {
      window.__actsSwReloadHooked = true;
      let reloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      });
    }
    return reg;
  } catch (err) {
    console.warn('[push] sw register failed:', err);
    return null;
  }
}

/** Convert a base64-url VAPID public key to the Uint8Array PushManager wants. */
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Returns true if the browser already has an active subscription for this app. */
export async function isPushSubscribed() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

/**
 * Full enable flow. Throws on the failure modes the UI needs to handle:
 *   - 'NotSupported' → browser doesn't support push at all
 *   - 'PermissionDenied' → user said "Block" in the OS prompt
 *   - 'PermissionDismissed' → user closed the prompt without choosing
 *   - 'NotConfigured' → server doesn't have VAPID keys set
 *
 * Returns the saved subscription on success.
 */
export async function enablePushNotifications() {
  if (!isPushSupported()) {
    const e = new Error('Seu navegador não suporta notificações push.');
    e.code = 'NotSupported'; throw e;
  }

  // 1. Make sure SW is registered + ready
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) reg = await registerServiceWorker();
  await navigator.serviceWorker.ready;

  // 2. Ask the OS for notification permission
  const perm = await Notification.requestPermission();
  if (perm === 'denied') {
    const e = new Error('Permissão negada. Habilite notificações nas configurações do navegador.');
    e.code = 'PermissionDenied'; throw e;
  }
  if (perm !== 'granted') {
    const e = new Error('Permissão não concedida. Tente de novo e escolha "Permitir".');
    e.code = 'PermissionDismissed'; throw e;
  }

  // 3. Pull the server's VAPID public key
  let vapid;
  try { vapid = await api.getVapidKey(); }
  catch (err) {
    const e = new Error('Servidor não configurou notificações ainda (peça pro admin).');
    e.code = 'NotConfigured'; throw e;
  }
  if (!vapid?.key) {
    const e = new Error('VAPID key vazia.');
    e.code = 'NotConfigured'; throw e;
  }

  // 4. Subscribe via PushManager
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.key),
    });
  }

  // 5. Send the subscription to the backend so it can push to us later
  const json = sub.toJSON();
  await api.subscribePush({
    endpoint:  json.endpoint,
    keys:      { p256dh: json.keys.p256dh, auth: json.keys.auth },
    userAgent: navigator.userAgent,
  });

  return sub;
}

export async function disablePushNotifications() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;

  // Best-effort: tell server first (so it stops sending), then unsubscribe locally
  try { await api.unsubscribePush(sub.endpoint); } catch { /* ignore */ }
  await sub.unsubscribe();
  return true;
}

/** Send a "did this work?" test push to the current user. */
export async function sendTestPushToSelf() {
  return api.testPush();
}
